import { Chip, Stack, Tooltip, Typography } from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import { AppDataTable, AppStatusSwitch } from "../../../../components/common";
import { SparePart } from "../types";

interface SparePartTableProps {
  rows: SparePart[];
  loading: boolean;
  page: number;
  pageSize: number;
  rowCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onEdit: (sparePart: SparePart) => void;
  onDelete: (sparePart: SparePart) => void;
  onStatusChange: (sparePart: SparePart, checked: boolean) => void;
  onView: (sparePart: SparePart) => void;
}

const SparePartTable = ({
  rows,
  loading,
  page,
  pageSize,
  rowCount,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onDelete,
  onStatusChange,
  onView,
}: SparePartTableProps) => {
  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Part Name",
      flex: 1.4,
      minWidth: 220,
    },
    {
      field: "code",
      headerName: "Code",
      width: 130,
    },
    {
      field: "categoryName",
      headerName: "Category",
      flex: 1,
      minWidth: 170,
    },
    {
      field: "currentStock",
      headerName: "Hub Stock",
      width: 170,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const stock = params.row.currentStock || 0;
        const reorder = params.row.reorderLevel || 0;
        const hubCount = params.row.stockedHubCount || 0;
        const label =
          stock === 0
            ? "No hub stock"
            : `${stock} in ${hubCount} hub${hubCount === 1 ? "" : "s"}`;
        const color = stock === 0 ? "error" : stock <= reorder ? "warning" : "success";

        return (
          <Tooltip title={params.row.hubStockSummary || "No hub stock yet"}>
            <Chip label={label} color={color} size="small" />
          </Tooltip>
        );
      },
    },
    {
      field: "hubStockSummary",
      headerName: "Available Hubs",
      flex: 1.5,
      minWidth: 240,
      sortable: false,
      renderCell: (params) => (
        <Stack sx={{ minWidth: 0 }}>
          <Tooltip title={params.row.hubStockSummary || "No hub stock yet"}>
            <Typography noWrap variant="body2">
              {params.row.hubStockSummary || "No hub stock yet"}
            </Typography>
          </Tooltip>
        </Stack>
      ),
    },
    {
      field: "minimumStock",
      headerName: "Min Stock",
      width: 120,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "reorderLevel",
      headerName: "Reorder",
      width: 120,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <span onClick={(event) => event.stopPropagation()}>
          <AppStatusSwitch
            checked={params.row.status === "ACTIVE"}
            onChange={(checked) => onStatusChange(params.row, checked)}
          />
        </span>
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 110,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={(event) => {
            event.stopPropagation();
            onEdit(params.row);
          }}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={(event) => {
            event.stopPropagation();
            onDelete(params.row);
          }}
        />,
      ],
    },
  ];

  return (
    <AppDataTable
      rows={rows}
      columns={columns}
      loading={loading}
      page={page}
      pageSize={pageSize}
      rowCount={rowCount}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      onRowClick={(params) => onView(params.row)}
    />
  );
};

export default SparePartTable;
